import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';

export interface DropdownOption {
    value: string;
    label: string;
    icon?: React.ElementType;
    badge?: string; // e.g., "RECOMENDADO"
    badgeColor?: string; // e.g., "bg-gradient-to-r from-violet-600 to-purple-600"
}

interface PremiumDropdownProps {
    value?: string;
    onChange: (value: string) => void;
    options: DropdownOption[];
    placeholder?: string;
    searchable?: boolean;
    disabled?: boolean;
    className?: string;
    label?: string; // Optional label above the dropdown
}

export function PremiumDropdown({
    value,
    onChange,
    options,
    placeholder = 'Selecione uma opção',
    searchable = false,
    disabled = false,
    className = '',
    label
}: PremiumDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(opt => opt.value === value);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className={className}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {label}
                </label>
            )}
            <div className="relative group" ref={dropdownRef}>
                <div className={`rounded-full p-[1px] border-0 ${disabled ? 'bg-gray-100 dark:bg-gray-800' : 'bg-gray-200 dark:bg-gray-800 dark:has-[+div]:bg-gradient-to-br dark:has-[+div]:from-blue-400/50 dark:has-[+div]:via-purple-500/50 dark:has-[+div]:to-pink-400/40'}`}>
                    <button
                        type="button"
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        disabled={disabled}
                        className="w-full px-4 py-2.5 bg-white dark:bg-[#030712] rounded-full focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none active:ring-0 active:outline-none text-left flex items-center justify-between shadow-sm transition-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            {selectedOption ? (
                                <>
                                    {selectedOption.icon && <selectedOption.icon className="w-5 h-5 flex-shrink-0" />}
                                    <span className={`block truncate text-sm text-gray-900 dark:text-white font-medium`}>
                                        {selectedOption.label}
                                    </span>
                                    {selectedOption.badge && (
                                        <span className={`ml-auto px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-white rounded-full shadow-sm border border-white/10 flex-shrink-0 ${selectedOption.badgeColor || 'bg-gradient-to-r from-violet-600 to-purple-600'}`}>
                                            {selectedOption.badge}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="text-gray-500 dark:text-gray-400 text-sm">
                                    {placeholder}
                                </span>
                            )}
                        </div>
                        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                    </button>
                </div>

                {isOpen && !disabled && (
                    <div className="absolute z-50 mt-1 w-full bg-white dark:bg-black shadow-xl max-h-96 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-hidden focus:outline-none sm:text-sm border border-gray-200 dark:border-gray-700 animate-in fade-in zoom-in-95 duration-100">
                        {searchable && (
                            <div className="sticky top-0 z-10 bg-white dark:bg-black px-2 py-2 border-b border-gray-200 dark:border-gray-700">
                                <div className="relative p-[1px] rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
                                    <div className="relative bg-white dark:bg-black rounded-full">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Search className="h-3.5 w-3.5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            role="combobox"
                                            aria-autocomplete="list"
                                            aria-expanded={isOpen}
                                            className="block w-full pl-9 pr-3 py-1.5 bg-transparent border-0 rounded-full leading-5 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 sm:text-sm"
                                            placeholder="Buscar..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option) => {
                                    const isSelected = option.value === value;
                                    return (
                                        <div
                                            key={option.value}
                                            className={`cursor-pointer select-none relative py-2.5 pl-3 pr-9 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors ${isSelected ? 'bg-blue-50 dark:bg-[#030712]' : ''}`}
                                            onClick={() => {
                                                onChange(option.value);
                                                setIsOpen(false);
                                                setSearchTerm('');
                                            }}
                                        >
                                            {option.icon && <option.icon className="w-5 h-5 flex-shrink-0" />}
                                            <span className={`block truncate ${isSelected ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                                                {option.label}
                                            </span>
                                            {option.badge && (
                                                <span className={`ml-auto px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-white rounded-full shadow-sm border border-white/10 ${option.badgeColor || 'bg-gradient-to-r from-violet-600 to-purple-600'}`}>
                                                    {option.badge}
                                                </span>
                                            )}
                                            {isSelected && (
                                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600 dark:text-blue-400">
                                                    <Check className="h-4 w-4" />
                                                </span>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="cursor-default select-none relative py-3 px-3 text-gray-500 dark:text-gray-400 text-center text-sm">
                                    Nenhuma opção encontrada
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
